import { Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { DeleteFormService } from './delete-form.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { SubmissionService } from '../../submission-data/submission.service';
import {
  buildHeaderDataShell,
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-efile-delete-form',
    templateUrl: './delete-form.component.html',
    styleUrls: ['./delete-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DeleteFormComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private subs = new Subscription();
  private isInitialized = false;
  private isLoading = false;

  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: ''
  };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  filterParams: any = {};

  /**
   * Concrete submission context — sourced from {@link SubmissionService.activeSubmission}
   * (Pattern B deep-links don't carry a grid `rowData`, so we mirror the
   * legacy `EfileFactory.selectedSubmission` access used in
   * EfileElementsController.js L1844-L1849).
   */
  submissionId: string = '';
  ssoId: string = '';

  formId: string = '';
  availableList: any[] = [];
  formElements: any[] = [];
  loading: boolean = false;
  loadingGrid: boolean = false;
  searchFilter: string = '';
  sortField: string = 'ELEMENT_NAME';
  sortDirection: 'asc' | 'desc' = 'asc';
  tableColumns = [
    { title: 'Element Name', field: 'ELEMENT_NAME' },
    { title: 'SNO', field: 'SNO' },
    { title: 'Element Value', field: 'ELEMENT_VALUE' }
  ];

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private deleteFormService: DeleteFormService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private submissionService: SubmissionService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext()));
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext()));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.USER_SETTINGS = shell.userSettingsData;
    this.SERVER_CONFIG = shell.serverConfigData;
    this.GENERAL_CONFIG = shell.generalConfigData;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);

    // Pattern B deep-links land on this component without a grid selection,
    // so the workspaceFactory rowData is empty. Source the submission context
    // from SubmissionService.activeSubmission — same value the legacy
    // controller pulled from EfileFactory.selectedSubmission (L1844).
    const activeSubmission: any = this.submissionService?.activeSubmission || ({} as any);
    const gridRow = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(activeScreen));
    this.rowData = {
      ...gridRow,
      SUBMISSION_ID: activeSubmission.SUBMISSION_ID ?? gridRow?.SUBMISSION_ID ?? gridRow?.submission_id ?? '',
      TAX_YEAR: activeSubmission.TAX_YEAR ?? gridRow?.TAX_YEAR ?? '',
      SUBMISSION_STATUS: activeSubmission.SUBMISSION_STATUS ?? gridRow?.SUBMISSION_STATUS ?? '',
    };
    this.submissionId = String(this.rowData.SUBMISSION_ID || '');
    this.ssoId = this.USER_SETTINGS?.user?.sso_id || '';

    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    const hasBaseUrl = (this.baseURLs?.custom || this.baseURLs?.api || this.GENERAL_CONFIG?.base_url);
    // Gate strictly on having a submissionId + base URL — legacy
    // DeleteFormController called loadForms() unconditionally on init using
    // EfileFactory.selectedSubmission.SUBMISSION_ID (L1894), so we mirror that.
    if (!hasBaseUrl || !this.submissionId) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  init(): void {
    this.loadForms();
  }

  loadForms(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs.custom || this.baseURLs.api;
    if (!baseUrl || !this.submissionId) {
      return;
    }

    this.loadingGrid = true;
    this.cdr.detectChanges();

    this.deleteFormService.loadForms(baseUrl, this.submissionId, this.ssoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.availableList = response?.jsonObject || [];
        this.loadingGrid = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.loadingGrid = false;
        this.gtwSnackbarService.openSnackBar('Failed to load forms.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  formChanged(formId: string): void {
    if (!formId) {
      this.formElements = [];
      this.cdr.detectChanges();
      return;
    }

    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs.custom || this.baseURLs.api;
    if (!baseUrl) {
      return;
    }

    this.loadingGrid = true;
    this.cdr.detectChanges();

    this.deleteFormService.loadFormElements(baseUrl, formId, this.ssoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.formElements = response?.jsonObject || [];
        this.loadingGrid = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.loadingGrid = false;
        this.gtwSnackbarService.openSnackBar('Failed to load form elements.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  deleteForm(): void {
    if (!this.formId) {
      return;
    }

    const efileApiUrl = this.GENERAL_CONFIG?.gtw_efile_api_url ?? this.baseURLs.efile ?? '';
    if (!efileApiUrl || !this.submissionId) {
      this.gtwSnackbarService.openSnackBar('Configuration error: missing API URL or submission ID.', 'gtw-snackbar--red');
      return;
    }

    this.loading = true;
    this.cdr.detectChanges();

    this.deleteFormService.deleteForm(efileApiUrl, this.submissionId, this.formId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        this.loading = false;
        const callSuccess = res?.callSuccess ?? res?.data?.callSuccess;
        if (callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('The form was successfully deleted!', 'gtw-snackbar--green');
          this.formElements = [];
          this.formId = '';
          this.loadForms();
          this.requestGridUpdate.emit('refresh');
        } else {
          const errorMessage = res?.errorMessage ?? res?.data?.errorMessage ?? 'Delete failed.';
          this.gtwSnackbarService.openSnackBar(errorMessage, 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      (err) => {
        this.loading = false;
        this.gtwSnackbarService.openSnackBar('Something went wrong! Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Defensive accessors — the `tfmy4n` SP response columns aren't strictly
   * documented and have shifted between revisions (FORM_ID vs ID, FORM vs
   * FORM_NAME). Read with the legacy convention first, then fall back so
   * the dropdown stays populated even if the SP is updated.
   */
  formItemId(item: any): string {
    return String(item?.FORM_ID ?? item?.form_id ?? item?.ID ?? item?.id ?? '');
  }

  formItemLabel(item: any): string {
    return String(
      item?.FORM ?? item?.FORM_NAME ?? item?.form ?? item?.form_name ?? item?.NAME ?? item?.name ?? this.formItemId(item)
    );
  }

  get filteredFormElements(): any[] {
    const term = this.searchFilter.toLowerCase().trim();
    const source = !this.searchFilter || !this.searchFilter.trim()
      ? this.formElements
      : this.formElements.filter(
      (row: any) =>
        (row.ELEMENT_NAME || '').toLowerCase().includes(term) ||
        (row.SNO || '').toString().toLowerCase().includes(term) ||
        (row.ELEMENT_VALUE || '').toLowerCase().includes(term)
    );
    const sorted = [...source];
    sorted.sort((a: any, b: any) => {
      const av = this.sortValue(a?.[this.sortField]);
      const bv = this.sortValue(b?.[this.sortField]);
      if (av < bv) return this.sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }

  sortBy(field: string): void {
    if (!field) return;
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.cdr.markForCheck();
  }

  getSortIconClass(field: string): string {
    if (this.sortField !== field) return 'fa-sort';
    return this.sortDirection === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc';
  }

  private sortValue(value: any): string | number {
    const asNumber = Number(value);
    if (value !== null && value !== undefined && value !== '' && Number.isFinite(asNumber)) {
      return asNumber;
    }
    return String(value ?? '').toLowerCase();
  }
}
